/**
 * BillingPage — CONTAINER da página de compra (/billing). Plano 19, TODO 1.3.
 *
 * Recorte 1: `?product=whatsapp` → pacotes de mensagens WhatsApp. É o destino do CTA "comprar
 * créditos" do push, do sino, do e-mail das 8h e do chip do topo. Vai virar a área de compras da
 * conta (produtos, pedidos, histórico) — a rota já nasceu com o nome definitivo porque
 * `notifications.action_url` é persistida e link antigo não pode quebrar.
 *
 * Contrato: pages/billing/billing.vm.js (@version 3). A View (BillingView) é do Claude Design.
 * Todo dado derivado (quanto dura, economia, ritmo) é calculado AQUI — a View nunca faz conta.
 *
 * Fontes (todas já existiam — nada de endpoint novo):
 *   useMeter('whatsapp_messages')            saldo, low e `lost` (perdas desde o último crédito)
 *   billingService.listProducts(meter)       catálogo
 *   whatsappService.getCreditStatement(30)   consumo por dia, lotes (validade), compras (PIX pendente)
 *   ['whatsapp-settings'] (react-query)      integração ligada? — mesma chave do Layout, cache compartilhado
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import billingService from '../services/billingService';
import whatsappService from '../services/whatsappService';
import { useMeter } from '../hooks/useEntitlements';
import useSnackbar from '../hooks/useSnackbar';
import usePackPurchase from '../hooks/usePackPurchase';
import AppSnackbar from '../components/ui/AppSnackbar';
import PixQrCheckout from '../components/billing/PixQrCheckout';
import DadosDeCobrancaDialog from '../components/billing/DadosDeCobrancaDialog';
import { trackEvent } from '../utils/tracking';
import BillingView from './billing/BillingView';
import { montarVm, METER, WINDOW_DAYS } from './billing/billingVmBuilder';

export default function BillingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const product = searchParams.get('product');
  const ref = searchParams.get('ref');
  const { snackbar, showSuccess, showError, hideSnackbar } = useSnackbar();

  // ── saldo / perdas (snapshot de entitlements, polling de 60 s) ─────────────────────────────
  const { balance, low, lost: lostRaw, loading: meterLoading } = useMeter(METER);
  const lost = Number(lostRaw ?? 0);

  // ── integração ligada? mesma chave do Layout: react-query compartilha o cache ─────────────
  const { data: waSettings } = useQuery({
    queryKey: ['whatsapp-settings'],
    queryFn: () => whatsappService.getSettings(),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });

  // ── catálogo + extrato ───────────────────────────────────────────────────────────────────
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [statement, setStatement] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  // Meios liberados para o pacote (Billing:PackMethods): só muda a dica da loja; a escolha é na modal.
  const [paymentMethods, setPaymentMethods] = useState(['Pix']);

  const load = useCallback(async () => {
    setProductsLoading(true); setErrorMessage(null);
    try {
      const [prods, st, meios] = await Promise.all([
        billingService.listProducts(METER),
        whatsappService.getCreditStatement(WINDOW_DAYS).catch(() => null), // extrato é enriquecimento: sem ele a página ainda vende
        billingService.getPaymentMethods('pack').catch(() => ['Pix']),
      ]);
      setProducts(Array.isArray(prods) ? prods : []);
      setStatement(st);
      setPaymentMethods(meios);
    } catch {
      setProducts([]);
      setErrorMessage('Não foi possível carregar os pacotes. Tente de novo.');
    } finally { setProductsLoading(false); }
  }, []);
  useEffect(() => { if (product === 'whatsapp') load(); }, [product, load]);

  // Atribuição: de qual aviso (ref=<msgId> | report-<yyyyMMdd>) veio a visita.
  useEffect(() => {
    if (product === 'whatsapp') trackEvent('billing_page_view', { product, ref: ref || null });
  }, [product, ref]);

  // ── compra (PIX ou cartão — fluxo compartilhado com Integrações) ─────────────────────────
  const compra = usePackPurchase({ onPaid: load, showError, showSuccess });

  // ── derivações — TODAS no builder puro (testado em __tests__); a View só lê ──────────────
  const vm = useMemo(() => montarVm({
    balance, low, lost, meterLoading,
    products, productsLoading, statement, errorMessage,
    ref, integrationEnabled: waSettings?.enabled, paymentMethods,
  }), [balance, low, lost, meterLoading, products, productsLoading, statement, errorMessage, ref, waSettings, paymentMethods]);

  const actions = {
    onBuyPackage: (id) => {
      const p = products.find((x) => String(x.id) === String(id));
      if (!p) return;
      trackEvent('billing_buy_click', { product: 'whatsapp', packageId: id, ref: ref || null });
      compra.handleBuy(p);
    },
    // Retomar a compra pendente = pedir o MESMO produto pelo MESMO meio: no PIX a API devolve o mesmo
    // QR (ou alreadyPaid); no cartão cria um pedido novo e volta à página de pagamento.
    onResumePurchase: () => {
      const pid = vm.pendingPurchase?.productId;
      const p = products.find((x) => String(x.id) === pid);
      if (!p) { showError('Não encontramos o pacote deste pagamento. Escolha um pacote abaixo.'); return; }
      const meio = vm.pendingPurchase?.method ?? 'Pix';
      trackEvent('billing_resume_click', { product: 'whatsapp', packageId: pid, method: meio });
      // sem modal (docs/PLANO-CARTAO-PACOTES.md, TODO 2.2), pela MESMA forma (docs/PLANO-MEIOS-DE-PAGAMENTO-NO-BANCO.md)
      compra.handleResume(p, meio, vm.pendingPurchase?.optionCode ?? undefined);
    },
    onRetry: load,
    onGoToIntegrations: () => navigate('/integrations'),
    onContactSupport: () => {
      const btn = document.querySelector('.agendario-support-widget-button');
      if (btn && typeof btn.click === 'function') btn.click();
      else navigate('/support-tickets');
    },
  };

  // Só WhatsApp por enquanto. Sem produto (ou produto desconhecido) → Integrações, sem tela em branco.
  if (product !== 'whatsapp') return <Navigate to="/integrations" replace />;

  return (
    <>
      <BillingView vm={vm} actions={actions} />
      <DadosDeCobrancaDialog {...compra.cobranca} />
      {compra.checkoutOrder && <PixQrCheckout order={compra.checkoutOrder} onClose={compra.closeCheckout} />}
      <AppSnackbar snackbar={snackbar} onClose={hideSnackbar} />
    </>
  );
}
